import {isObject} from "lodash-es"
import {Feature} from "ol"
import {Geometry, LineString, Point, Polygon} from "ol/geom"
import {FeatureLike} from "ol/Feature"
import {Coordinate} from "ol/coordinate"
import VectorLayer from "ol/layer/Vector"
import {t} from "@ui/common/intl"
import mapService from "@ui/services/MapService"
import {FeatureLayers, LayerId} from "./"

export type TPropDesc = {
  name: string                  // название свойства
  isData?: boolean              // флаг, указывает что значение является данными, и его не надо локализовывать
  hasNational?: boolean         // флаг указывает, что есть объект с именем national_{name}, в котором локализованная информация
  withoutTitle?: boolean        // название свойства без локализации
  isDictionary?: boolean        // флаг, который убирает название объекта из ключа для локализации тайтла и значения - ${objName}
}
export type TPopupData = {
  name: string                      // название объекта
  obj: {[key: string]: any}         // объект, свойства которого выводим в попапе
  propNames: (string | TPropDesc)[] // названия свойств объекта, которые выводим в попапе
}

export default class FeatureService {

  static getLayer(id: string) {
    let layer = mapService.getLayerById<VectorLayer<Feature>>(id)
    if (!layer) {
      layer = FeatureLayers[id]
      mapService.addLayer(layer)
    }
    return layer
  }

  static addFeatures<T extends Geometry>(layerId: LayerId, features: Feature<T>[]) {
    FeatureService.getLayer(layerId).getSource()
      ?.addFeatures(features.map(feature => {
        feature.setProperties({layerId})
        return feature
      }))
  }

  static getFeatureCoordinates(feature: FeatureLike): Coordinate | undefined {
    switch (feature.getGeometry()?.getType()) {
      case "Point":
        return (feature as Feature<Point>).getGeometry()?.getCoordinates()
      case "LineString":
        return (feature as Feature<LineString>).getGeometry()?.getCoordinateAt(0.5)
      case "Polygon":
        return (feature as Feature<Polygon>).getGeometry()?.getInteriorPoint().getCoordinates()
    }
  }

  static constructFeaturePopup(data: TPopupData[]) {
    const html = data.map((data, i) => this.constructObjPopup(data)).join("")

    return `
        <div class="feature-popup-form">
          ${html ? html : t("feature.popup.no.data")}
        </div>
      `
  }

  static constructObjPopup({name, obj, propNames}: TPopupData) {
    if (!obj) return ""
    const html = (Array.isArray(obj) ? obj : [obj]).map(obj =>
      propNames.map(propName => this.constructPopupRow(obj, propName, name)).join("")
    ).join("")

    return html
      ? `
          <div class="row header">${t(`feature.popup.${name}.title`)}</div>
          ${html}
      `
      : ""
  }

  static constructPopupRow(obj: any, propData: string | TPropDesc, objName: string) {
    const isPropData = isObject(propData)
    const propName = isPropData ? propData.name : propData
    const prop = isPropData && propData.hasNational ? obj[`national_${propName}`] || obj[propName] : obj[propName]
    const isDictionary = isPropData && propData.isDictionary
    const withoutTitle = isPropData && propData.withoutTitle
    const isLocalize = isPropData && (propData.isData || propData.hasNational)
    const i18nPrefix = `feature.popup${isDictionary ? "" : `.${objName}`}.${propName}`

    return prop !== undefined && prop !== null  && (!Array.isArray(prop) || prop.length)
      ? `
        <div class="row">
          <div>${withoutTitle ? propName : t(`${i18nPrefix}.title`)}</div>
          ${
            Array.isArray(prop)
              ? `<div>${prop.map((elem: string) => t(`${i18nPrefix}.${elem}`)).join(", ")}</div>`
              : `<div class="${propName}">${isLocalize ? prop : t(`${i18nPrefix}.${prop}`)}</div>`
          }
        </div>
      `
      : ""
  }
}
